import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Client, ClientStatus, CreateClientDto, UpdateClientDto } from 'api';
import { clientService } from 'api';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { queryKeys } from 'shared/constants/queryKeys';

interface DeleteDialogState {
  open: boolean;
  clientId: string | null;
}

interface StatusDialogState {
  open: boolean;
  clientId: string | null;
  targetStatus: ClientStatus | null;
  pendingData: UpdateClientDto | null;
}

interface ClientModalState {
  open: boolean;
  clientId: string | null;
  initialName: string;
}

/**
 * Client list + the Edit/Status/Delete flows for a single client — extracted so
 * ClientsPage (list) and ClientDetailPage (hero "more" menu) drive the exact same
 * modal, dialogs, mutations, and cache invalidation instead of each having its own copy.
 */
export function useClientActions() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    clientId: null,
  });
  const [statusDialog, setStatusDialog] = useState<StatusDialogState>({
    open: false,
    clientId: null,
    targetStatus: null,
    pendingData: null,
  });
  const [clientModal, setClientModal] = useState<ClientModalState>({
    open: false,
    clientId: null,
    initialName: '',
  });

  const { data: clients = [], isLoading } = useQuery({
    queryKey: queryKeys.clients.list(),
    queryFn: () => clientService.getClients().then((res) => res.data.data),
  });

  const closeModal = () => setClientModal({ open: false, clientId: null, initialName: '' });

  const createMutation = useMutation({
    mutationFn: (data: CreateClientDto) => clientService.createClient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      closeModal();
      enqueueSnackbar('Client created', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to create client', { variant: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClientDto }) =>
      clientService.updateClient(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      closeModal();
      enqueueSnackbar('Client updated', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to update client', { variant: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => clientService.deleteClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      enqueueSnackbar('Client deleted', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to delete client', { variant: 'error' });
    },
  });

  // ── Edit ──────────────────────────────────────────────────────────────────

  const openCreateModal = () => setClientModal({ open: true, clientId: null, initialName: '' });
  const openEditModal = (clientId: string) => setClientModal({ open: true, clientId, initialName: '' });

  const handleModalSubmit = (data: CreateClientDto | UpdateClientDto) => {
    if (clientModal.clientId) {
      const updateData = data as UpdateClientDto;
      // Changing to inactive cascades to projects — confirm first
      if (updateData.status === 'Inactive') {
        const currentClient = clients.find((c) => c.id === clientModal.clientId);
        if (currentClient?.status !== 'Inactive') {
          closeModal();
          setStatusDialog({ open: true, clientId: clientModal.clientId, targetStatus: 'Inactive', pendingData: updateData });
          return;
        }
      }
      updateMutation.mutate({ id: clientModal.clientId, data: updateData });
    } else {
      createMutation.mutate(data as CreateClientDto);
    }
  };

  // ── Status change confirm (cascade warning when going inactive) ──────────

  const confirmStatusChange = () => {
    if (!statusDialog.clientId) return;
    const client = clients.find((c) => c.id === statusDialog.clientId);
    if (!client) return;
    const data: UpdateClientDto = statusDialog.pendingData ?? {
      name: client.name,
      email: client.email,
      address: client.address,
      note: client.note,
      currency: client.currency,
      status: statusDialog.targetStatus ?? 'Inactive',
    };
    updateMutation.mutate(
      { id: statusDialog.clientId, data },
      { onSettled: () => setStatusDialog({ open: false, clientId: null, targetStatus: null, pendingData: null }) },
    );
  };

  const cancelStatusChange = () => setStatusDialog({ open: false, clientId: null, targetStatus: null, pendingData: null });

  // ── Delete ───────────────────────────────────────────────────────────────

  const requestDelete = (clientId: string) => setDeleteDialog({ open: true, clientId });

  /** @param onDeleted Extra side effect run only on a successful delete (e.g. navigate away). */
  const confirmDelete = (onDeleted?: () => void) => {
    if (!deleteDialog.clientId) return;
    deleteMutation.mutate(deleteDialog.clientId, {
      onSuccess: () => onDeleted?.(),
      onSettled: () => setDeleteDialog({ open: false, clientId: null }),
    });
  };

  const cancelDelete = () => setDeleteDialog({ open: false, clientId: null });

  const getClient = (clientId: string): Client | undefined => clients.find((c) => c.id === clientId);

  return {
    clients,
    isLoading,
    getClient,

    clientModal,
    openCreateModal,
    openEditModal,
    closeModal,
    handleModalSubmit,
    createMutation,
    updateMutation,

    statusDialog,
    confirmStatusChange,
    cancelStatusChange,

    deleteDialog,
    requestDelete,
    confirmDelete,
    cancelDelete,
    deleteMutation,
  };
}
